import React from "react";
import { motion } from "framer-motion";

import RichText from "../i18n/RichText";
import { useLanguage } from "../i18n/LanguageContext";

const Footer = () => {
  const { t } = useLanguage();

  return (
    <div className="bg-[#002E3F] w-full px-5 2xl:px-10 3xl:px-20 py-12 overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-4 w-full mb-10 md:mb-16 gap-y-5 md:gap-y-3 xl md:gap-x-5 lg:gap-x-0">
        <motion.div
          className="flex flex-col gap-y-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="helvetica-500 text-[#FFF1E1] text-base lg:text-xl 2xl:text-2xl text-center md:text-start tracking-wide">
            <RichText text={t("footer.helpTitle")} />
          </div>
          <div className="helvetica-400 text-[#FFF1E1] text-[12px] lg:text-sm text-center md:text-start tracking-widest">
            <RichText text={t("footer.helpText")} />
          </div>
        </motion.div>

        <motion.div
          className="helvetica-400 text-[#FFF1E1] text-[12px] xl:text-sm leading-relaxed tracking-widest text-center md:text-start"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {t("footer.hotline")}
          <br />
          (0251) 8311362
        </motion.div>
        <motion.div
          className="helvetica-400 text-[#FFF1E1] text-[12px] xl:text-sm leading-relaxed tracking-widest text-center md:text-start"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {t("footer.email")}
          <br />
          info@lifesciencestudio.id
        </motion.div>

        <div className="flex flex-col lg:flex-row justify-between items-center md:items-start gap-y-5 lg:gap-y-0">
          <motion.div
            className="flex flex-col items-center md:items-start gap-y-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="helvetica-400 text-[#FFF1E1] tracking-widest text-sm">
              {t("footer.connect")}
            </div>
            <div className="leading-tight helvetica-400 text-[#FFF1E1] tracking-wider text-lg xl:text-xl text-center md:text-start">
              <RichText text={t("footer.handle")} />
            </div>
          </motion.div>
          <div className="flex justify-between gap-x-3 3xl:gap-x-5 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <a
                href="https://www.facebook.com/p/Riwayat-Purba-61590063821356/"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="object-contain w-3/4 md:w-[90%] 3xl:w-full mx-auto md:mx-0"
                  src="/assets/images/facebook.png"
                  alt="Facebook Riwayat Purba"
                  width="75"
                  height="75"
                />
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <a
                href="https://www.instagram.com/riwayat_purba/"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="object-contain w-3/4 md:w-[90%] 3xl:w-full mx-auto md:mx-0"
                  src="/assets/images/instagram.png"
                  alt="Instagram @riwayat_purba"
                  width="75"
                  height="75"
                />
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              <a
                href="https://www.tiktok.com/@riwayat_purba"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="object-contain w-3/4 md:w-[90%] 3xl:w-full mx-auto md:mx-0"
                  src="/assets/images/tiktok.png"
                  alt="TikTok @riwayat_purba"
                  width="75"
                  height="75"
                />
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between w-full items-center gap-y-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 10 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <img
            className="w-1/2 3xl:w-[90%] mx-auto md:ml-0 md:mr-auto"
            src="/assets/images/logo-purba.png"
            alt={t("alt.logoPurba")}
            width="317"
            height="95"
          />
        </motion.div>

        <div className="flex flex-col md:flex-row justify-between md:gap-x-0 3xl:gap-x-16 items-center gap-y-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 10 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <img
              className="w-1/2 md:w-[60%] 3xl:w-[90%] mx-auto"
              src="/assets/images/logo-kr.png"
              alt={t("alt.logoKebunRaya")}
              width="326"
              height="73"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 10 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <img
              className="w-1/2 md:w-[60%] 3xl:w-[90%] mx-auto"
              src="/assets/images/logo-dyandra.png"
              alt={t("alt.logoDyandra")}
              width="217"
              height="65"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 10 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <img
              className="w-1/2 md:w-[60%] 3xl:w-[90%] mx-auto"
              src="/assets/images/logo-lss.png"
              alt={t("alt.logoLifeScience")}
              width="172"
              height="119"
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
